/**
 * Date-normalising structural comparison used ONLY by the cross-tab adoption
 * path. Deliberately NOT `FilingFormStateService.deepEqual`: that helper treats
 * null/''/0/false as mutually equivalent so the pencil does not flip on form-init
 * noise, which would make adoption blind to a user genuinely clearing a field.
 * Kept DI-free so it can be unit-tested without TestBed.
 */

/** Matches an ISO-8601 date or date-time so both collapse to a bare calendar date. */
const ISO_DATE_LIKE = /^\d{4}-\d{2}-\d{2}(T.*)?$/;

export function normalizeForCompare(value: unknown): unknown {
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);

  if (typeof value === 'string' && ISO_DATE_LIKE.test(value))
    return value.slice(0, 10);

  if (Array.isArray(value))
    return value.map(normalizeForCompare);

  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    // Sort keys so structurally equal objects serialise identically.
    for (const key of Object.keys(source).sort())
      out[key] = normalizeForCompare(source[key]);
    return out;
  }

  return value;
}

export function slicesDiffer(previous: unknown, next: unknown): boolean {
  return JSON.stringify(normalizeForCompare(previous)) !== JSON.stringify(normalizeForCompare(next));
}

export function diffChangedSheets<T extends Record<string, unknown>>(
  prev: T,
  next: T
): (keyof T)[] {
  const keys = new Set([...Object.keys(prev ?? {}), ...Object.keys(next ?? {})]);
  return [...keys].filter(key => slicesDiffer(prev?.[key], next?.[key])) as (keyof T)[];
}
